import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/shared/api/client'
import { toast } from '@/shared/model/toast'

/* ---------------------------------------------------------------- типы */

export interface KV {
  key: string
  value: string
}

export interface Condition {
  path: string
  op: string
  value: string
}

export interface AuthConfig {
  kind: string
  headerName?: string
  queryName?: string
  username?: string
  authorizeUrl?: string
  tokenUrl?: string
  clientId?: string
  scope?: string
  connected?: boolean
  tokenMethod?: string
  tokenBody?: string
  tokenContentType?: string
  tokenPath?: string
  tokenTtl?: number
  tokenHeader?: string
  tokenPrefix?: string
  signatureHeader?: string
}

export type Secrets = Record<string, string>

export interface SecretHint {
  set: boolean
  hint: string
}

export interface Variable {
  path: string
  type: string
  sample: string
}

export interface EventInfo {
  type: string
  group: string
  title: string
  description: string
  sample: unknown
  variables: Variable[]
}

export interface ActionField {
  key: string
  title: string
  type: string
  required: boolean
  hint?: string
}

export interface ActionDef {
  action: string
  title: string
  description: string
  fields: ActionField[]
  settings: string[] | null
  sample: unknown
}

export interface BitrixPreset {
  key: string
  title: string
  description: string
  method: string
  body: string
  itemsPath: string
  perCall: boolean
}

export interface BitrixTemplate {
  key: string
  title: string
  description: string
  events: string[]
  filters: Condition[] | null
  flow: string
}

export interface Catalog {
  events: EventInfo[]
  authKinds: string[]
  actions: ActionDef[]
  schemaTypes: string[]
  bitrixPresets: BitrixPreset[]
  bitrixTemplates: BitrixTemplate[]
  operators: { op: string; title: string; noArg: boolean }[]
  campaigns: { id: number; name: string; status: string }[]
  bots: { id: number; name: string }[]
  publicUrl: string
  contactVariables: Variable[]
}

export interface WebhookStats {
  total: number
  success: number
  failed: number
  pending: number
  lastAt: string | null
}

export interface Webhook {
  id: number
  name: string
  enabled: boolean
  method: string
  url: string
  headers: KV[]
  auth: AuthConfig
  secrets: Record<string, SecretHint>
  events: string[]
  filters: Condition[]
  bodyMode: 'standard' | 'template' | 'bitrix'
  bodyTemplate: string
  pausedReason: string
  signingSecret: string
  stats: WebhookStats
  createdAt: string
  updatedAt: string
}

export interface WebhookInput {
  name: string
  enabled: boolean
  method: string
  url: string
  headers: KV[]
  auth: AuthConfig
  secrets: Secrets
  events: string[]
  filters: Condition[]
  bodyMode: 'standard' | 'template' | 'bitrix'
  bodyTemplate: string
}

export interface Attempt {
  at: string
  status: number
  error?: string
  durationMs: number
  manual?: boolean
}

export interface Delivery {
  id: number
  webhookId: number
  webhookName: string
  eventId: string
  eventType: string
  eventTitle: string
  isTest: boolean
  status: 'pending' | 'sending' | 'success' | 'retrying' | 'failed'
  attempts: number
  nextAt: string
  lastStatus: number
  lastError: string
  durationMs: number
  createdAt: string
  updatedAt: string
  payload?: unknown
  request?: { method?: string; url?: string; headers?: KV[]; body?: string; calls?: unknown[] }
  responseBody?: string
  history?: Attempt[]
}

export interface FieldInfo {
  path: string
  type: string
  sample: string
}

export interface ShapeField {
  path: string
  label: string
}

export interface Shape {
  itemsPath: string
  fields: ShapeField[]
  filters: Condition[]
  limit: number
  intro: string
}

export interface RequestConfig {
  method: string
  url: string
  headers?: KV[]
  body?: string
}

export interface Source {
  id: number
  name: string
  enabled: boolean
  kind: 'http' | 'bitrix'
  request: RequestConfig
  auth: AuthConfig
  secrets: Record<string, SecretHint>
  mode: 'per_call' | 'cached'
  refreshMin: number
  timeoutMs: number
  shape: Shape
  bots: number[]
  lastRunAt: string | null
  lastOkAt: string | null
  lastText: string
  lastError: string
  lastItems: number
  createdAt: string
  updatedAt: string
}

export interface SourceInput {
  name: string
  enabled: boolean
  kind: 'http' | 'bitrix'
  request: RequestConfig
  auth: AuthConfig
  secrets: Secrets
  mode: 'per_call' | 'cached'
  refreshMin: number
  timeoutMs: number
  shape: Shape
  bots: number[]
}

export interface SourceTestResult {
  ok: boolean
  status: number
  error: string
  durationMs: number
  request: { method?: string; url?: string; headers?: KV[]; body?: string }
  response: unknown
  rawBody: string
  fields: FieldInfo[]
  itemPaths: string[]
  items: number
  itemFields: FieldInfo[]
  text: string
  contactId: number
  contact: string
}

export interface SourceRun {
  id: number
  sourceId: number
  callId: number | null
  trigger: string
  ok: boolean
  status: number
  error: string
  items: number
  durationMs: number
  createdAt: string
}

export interface SchemaField {
  path: string
  type: string
  required: boolean
  enum?: string[]
  example?: string
}

export interface Mapping {
  source: string
  const?: string
  transform?: string
}

export interface InboundConfig {
  schema: SchemaField[]
  mapping: Record<string, Mapping>
  itemsPath: string
  campaignId: number
  idempotencyPath: string
  rateLimit: number
  signatureHeader: string
  queryName: string
  sampleFormat?: 'json' | 'form'
  sample: string
}

export interface Inbound {
  id: number
  name: string
  enabled: boolean
  action: string
  authMode: 'token' | 'header' | 'hmac' | 'none'
  config: InboundConfig
  url: string
  secret: string
  stats: Record<string, number>
  createdAt: string
  updatedAt: string
}

export interface InboundInput {
  name: string
  enabled: boolean
  action: string
  authMode: 'token' | 'header' | 'hmac' | 'none'
  config: InboundConfig
}

export interface InboundItem {
  index: number
  ok: boolean
  phone?: string
  did?: string
  error?: string
  fields?: { path: string; message: string }[]
  contactId?: number
}

export interface InboundResult {
  ok: boolean
  requestId?: number
  status: string
  message: string
  processed: number
  failed: number
  results?: InboundItem[]
  duplicate?: boolean
}

export interface InboundRequest {
  id: number
  inboundId: number
  ip: string
  contentType: string
  body: string
  status: string
  httpStatus: number
  message: string
  result: InboundResult | null
  idempotencyKey: string
  createdAt: string
}

export interface BitrixOption {
  id: string
  title: string
  group?: string
  type?: string
}

export interface BitrixMeta {
  portal: string
  categories: BitrixOption[]
  dealStages: BitrixOption[]
  leadStatuses: BitrixOption[]
  users: BitrixOption[]
  fields: { deal: BitrixOption[]; lead: BitrixOption[]; contact: BitrixOption[] }
  warnings: string[]
}

export interface BitrixStep {
  key: string
  title: string
  status: 'ok' | 'skipped' | 'failed' | 'planned'
  detail: string
  method?: string
  params?: unknown
}

export interface BitrixRun {
  steps: BitrixStep[]
  leadId?: number
  contactId?: number
  dealId?: number
}

export interface BitrixPreviewResult {
  run: BitrixRun | null
  error: string
  durationMs: number
  sample: boolean
  live: boolean
}

/* ----------------------------------------------------------------- API */

const base = '/integrations'

export const integrationsApi = {
  catalog: (signal?: AbortSignal) => api.get<Catalog>(`${base}/catalog`, signal),

  webhooks: (signal?: AbortSignal) =>
    api.get<{ items: Webhook[] }>(`${base}/webhooks`, signal).then((r) => r.items ?? []),
  webhook: (id: number) => api.get<Webhook>(`${base}/webhooks/${id}`),
  saveWebhook: (id: number | null, body: WebhookInput) =>
    id ? api.put<Webhook>(`${base}/webhooks/${id}`, body) : api.post<Webhook>(`${base}/webhooks`, body),
  deleteWebhook: (id: number) => api.delete<void>(`${base}/webhooks/${id}`),
  setWebhookEnabled: (id: number, on: boolean) =>
    api.post<Webhook>(`${base}/webhooks/${id}/${on ? 'enable' : 'disable'}`),
  rotateWebhook: (id: number) => api.post<Webhook>(`${base}/webhooks/${id}/rotate`),
  testWebhook: (id: number | null, event: string, draft?: WebhookInput) =>
    api.post<Delivery>(id ? `${base}/webhooks/${id}/test` : `${base}/webhooks/test`, { event, draft }),
  previewTemplate: (event: string, template: string) =>
    api.post<{ body: string }>(`${base}/webhooks/preview`, { event, template }),

  deliveries: (params: { webhook?: number; status?: string; q?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams()
    if (params.webhook) q.set('webhook', String(params.webhook))
    if (params.status) q.set('status', params.status)
    if (params.q) q.set('q', params.q)
    q.set('limit', String(params.limit ?? 30))
    q.set('offset', String(params.offset ?? 0))
    return api.get<{ items: Delivery[]; total: number }>(`${base}/deliveries?${q}`)
  },
  delivery: (id: number) => api.get<Delivery>(`${base}/deliveries/${id}`),
  retryDelivery: (id: number) => api.post<Delivery>(`${base}/deliveries/${id}/retry`),
  retryFailed: (webhookId: number, hours: number) =>
    api.post<{ queued: number }>(`${base}/deliveries/retry-failed`, { webhookId, hours }),

  bitrixMeta: (webhookId: number | null, secrets: Secrets) =>
    api.post<BitrixMeta>(`${base}/bitrix/meta`, { webhookId: webhookId ?? 0, secrets }),
  bitrixPreview: (
    webhookId: number | null,
    body: { secrets: Secrets; flow: unknown; event: string; callId?: number; live?: boolean },
  ) => api.post<BitrixPreviewResult>(`${base}/bitrix/preview`, { webhookId: webhookId ?? 0, ...body }),

  sources: (signal?: AbortSignal) =>
    api.get<{ items: Source[] }>(`${base}/sources`, signal).then((r) => r.items ?? []),
  saveSource: (id: number | null, body: SourceInput) =>
    id ? api.put<Source>(`${base}/sources/${id}`, body) : api.post<Source>(`${base}/sources`, body),
  deleteSource: (id: number) => api.delete<void>(`${base}/sources/${id}`),
  testSource: (id: number | null, source: SourceInput, contactId?: number) =>
    api.post<SourceTestResult>(`${base}/sources/test`, { id: id ?? 0, contactId: contactId ?? 0, source }),
  runSource: (id: number) => api.post<Source>(`${base}/sources/${id}/run`),
  sourceRuns: (id: number) =>
    api.get<{ items: SourceRun[]; total: number }>(`${base}/sources/${id}/runs?limit=30`),
  sourcesPreview: (contact?: number, bot?: number) => {
    const q = new URLSearchParams()
    if (contact) q.set('contact', String(contact))
    if (bot) q.set('bot', String(bot))
    return api.get<{ text: string }>(`${base}/sources/preview?${q}`)
  },

  inbound: (signal?: AbortSignal) =>
    api.get<{ items: Inbound[] }>(`${base}/inbound`, signal).then((r) => r.items ?? []),
  saveInbound: (id: number | null, body: InboundInput) =>
    id ? api.put<Inbound>(`${base}/inbound/${id}`, body) : api.post<Inbound>(`${base}/inbound`, body),
  deleteInbound: (id: number) => api.delete<void>(`${base}/inbound/${id}`),
  rotateInbound: (id: number, what: 'url' | 'secret') =>
    api.post<Inbound>(`${base}/inbound/${id}/rotate`, { what }),
  guessInbound: (action: string, sample: string, format: 'json' | 'form') =>
    api.post<{ schema: SchemaField[]; itemsPath: string; mapping: Record<string, Mapping> }>(
      `${base}/inbound/guess`,
      { action, sample, format },
    ),
  testInbound: (id: number, body: string, format: 'json' | 'form') =>
    api.post<InboundResult>(`${base}/inbound/${id}/test`, { body, format }),
  inboundRequests: (params: { inbound?: number; status?: string; offset?: number }) => {
    const q = new URLSearchParams()
    if (params.inbound) q.set('inbound', String(params.inbound))
    if (params.status) q.set('status', params.status)
    q.set('limit', '30')
    q.set('offset', String(params.offset ?? 0))
    return api.get<{ items: InboundRequest[]; total: number }>(`${base}/inbound/requests?${q}`)
  },

  oauthStart: (kind: 'webhook' | 'source', id: number) =>
    api.post<{ url: string }>(`${base}/oauth/start`, { kind, id }),
}

/* --------------------------------------------------------------- хуки */

export const ik = {
  all: ['integrations'] as const,
  catalog: ['integrations', 'catalog'] as const,
  webhooks: ['integrations', 'webhooks'] as const,
  deliveries: ['integrations', 'deliveries'] as const,
  sources: ['integrations', 'sources'] as const,
  inbound: ['integrations', 'inbound'] as const,
  inboundRequests: ['integrations', 'inbound-requests'] as const,
}

export function useCatalog() {
  return useQuery({
    queryKey: ik.catalog,
    queryFn: ({ signal }) => integrationsApi.catalog(signal),
    staleTime: 5 * 60_000,
  })
}

export function useWebhooks() {
  return useQuery({
    queryKey: ik.webhooks,
    queryFn: ({ signal }) => integrationsApi.webhooks(signal),
    refetchInterval: 30_000,
  })
}

export function useSources() {
  return useQuery({ queryKey: ik.sources, queryFn: ({ signal }) => integrationsApi.sources(signal) })
}

export function useInbound() {
  return useQuery({
    queryKey: ik.inbound,
    queryFn: ({ signal }) => integrationsApi.inbound(signal),
    refetchInterval: 30_000,
  })
}

export function useInvalidate() {
  const qc = useQueryClient()
  return () => void qc.invalidateQueries({ queryKey: ik.all })
}

export function errorText(error: unknown, fallback = 'Что-то пошло не так'): string {
  return error instanceof ApiError ? error.message : fallback
}

/** Мутация с тостами и обновлением всех списков интеграций. */
export function useAction<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  success?: string | ((r: TResult) => string | undefined),
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ik.all })
      const msg = typeof success === 'function' ? success(r) : success
      if (msg) toast.success(msg)
    },
    onError: (error) => toast.error(errorText(error)),
  })
}
